/**
 * Storefront API client for multi-store e-commerce platform.
 * Provides static methods for making authenticated API requests to the storefront backend.
 * 
 * @class
 * @description This class wraps the browser's fetch API to communicate with a custom
 *              storefront backend. It handles authentication via Bearer tokens and
 *              automatically parses JSON responses.
 * 
 * @requires window.api_base_url (global)
 * @requires window.auth_token (global)
 * 
 * @example
 * // Initialize with API credentials
 * MultiStorefront.init('https://api.example.com', 'your-auth-token');
 * 
 * // Make a GET request
 * const data = await MultiStorefront.get('/products');
 * 
 * // Make a POST request
 * const result = await MultiStorefront.post('/cart/add', { id: 123, quantity: 1 });
 */
window.MultiStorefront = class Storefront {
  /** @type {string} Base URL for API requests */
  static API_URL = '';
  /** @type {string} Authentication token */
  static token = '';

  /**
   * Initializes the Storefront API client with credentials.
   * Must be called before making any API requests.
   * 
   * @static
   * @param {string} url - Base API URL
   * @param {string} token - Authentication token
   * @returns {void}
   * 
   * @example
   * MultiStorefront.init('https://api.mystore.com', 'abc123token');
   */
  static init(url, token) {
    this.API_URL = `${url}/api/storefront`;
    this.token = token;
  }

  /**
   * Makes a fetch request with authentication headers.
   * 
   * @static
   * @async
   * @param {string} input - URL or Request object
   * @param {Object} [init] - Optional fetch init object
   * @returns {Promise<Object|string>} Parsed response body (JSON or text)
   * 
   * @example
   * const data = await MultiStorefront.fetch('/products', { method: 'POST', body: JSON.stringify({...}) });
   */
  static async fetch(input, init) {
    const response = await fetch(input, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      ...(init || {}),
    });

    // Try to parse as JSON, fall back to raw text
    const body = await response.text();
    try {
      return JSON.parse(body);
    } catch (err) {
      return body;
    }
  }

  /**
   * Makes a GET request to the API.
   * 
   * @static
   * @async
   * @param {string} url - API endpoint path (will be appended to API_URL)
   * @returns {Promise<Object>} Parsed JSON response
   * 
   * @example
   * const products = await MultiStorefront.get('/products');
   */
  static async get(url) {
    return await this.fetch(`${this.API_URL}${url}`);
  }

  /**
   * Makes a POST request to the API with JSON body.
   * 
   * @static
   * @async
   * @param {string} url - API endpoint path
   * @param {Object} [data] - Data to send as JSON body
   * @returns {Promise<Object>} Parsed JSON response
   * 
   * @example
   * const result = await MultiStorefront.post('/cart/add', { id: 123, quantity: 1 });
   */
  static async post(url, data) {
    return await this.fetch(`${this.API_URL}${url}`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }
};
